/**
 * 장비 인스턴스 생성·강화. Phaser 없음.
 */

import { RARITY, type EquipSlot, type RarityId } from '../data/equipment';
import { ITEMS, type ItemDef } from '../data/items';

export interface GearPiece {
  itemId: string;
  uid: string;
  rarity: RarityId;
  watk: number;
  def: number;
  acc: number;
  upgradesLeft: number;
  successCount: number;
}

function uid(): string {
  return `g${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

export function isGearItem(def: ItemDef | undefined): boolean {
  return Boolean(def?.equip?.slot);
}

export function pickRarity(rng: () => number = Math.random): RarityId {
  const list = Object.values(RARITY);
  const total = list.reduce((s, r) => s + r.weight, 0);
  let roll = rng() * total;
  for (const r of list) {
    roll -= r.weight;
    if (roll <= 0) return r.id as RarityId;
  }
  return 'common';
}

function rollStat(min: number, max: number, mul: number, rng: () => number): number {
  if (max <= 0 && min <= 0) return 0;
  const raw = min + rng() * (max - min);
  return Math.max(0, Math.round(raw * mul));
}

export function rollGear(itemId: string, rarity?: RarityId, rng: () => number = Math.random): GearPiece | null {
  const def = ITEMS[itemId];
  const eq = def?.equip;
  if (!eq?.slot) return null;
  const rare = rarity ?? pickRarity(rng);
  const mul = RARITY[rare]?.optMul ?? 1;
  return {
    itemId,
    uid: uid(),
    rarity: rare,
    watk: rollStat(eq.watkMin ?? 0, eq.watkMax ?? 0, mul, rng),
    def: rollStat(eq.defMin ?? 0, eq.defMax ?? 0, mul, rng),
    acc: rollStat(eq.accMin ?? 0, eq.accMax ?? 0, mul, rng),
    upgradesLeft: eq.upgrades ?? 5,
    successCount: 0,
  };
}

export function starterGear(itemId: string): GearPiece | null {
  const def = ITEMS[itemId];
  const eq = def?.equip;
  if (!eq?.slot) return null;
  return {
    itemId,
    uid: uid(),
    rarity: 'common',
    watk: 0,
    def: Math.floor(((eq.defMin ?? 0) + (eq.defMax ?? 0)) / 2),
    acc: 0,
    upgradesLeft: eq.upgrades ?? 5,
    successCount: 0,
  };
}

export function gearPrice(piece: GearPiece): number {
  const base = ITEMS[piece.itemId]?.price ?? 0;
  const rare = RARITY[piece.rarity]?.priceMul ?? 1;
  return Math.max(1, Math.round(base * rare));
}

export function sumWorn(worn: Partial<Record<EquipSlot, GearPiece | null>>): {
  watk: number;
  def: number;
  acc: number;
} {
  let watk = 0;
  let def = 0;
  let acc = 0;
  for (const piece of Object.values(worn)) {
    if (!piece) continue;
    watk += piece.watk;
    def += piece.def;
    acc += piece.acc;
  }
  return { watk, def, acc };
}

export function applyScrollToGear(
  piece: GearPiece,
  scroll: NonNullable<ItemDef['scroll']>,
  rng: () => number = Math.random,
): 'success' | 'fail' | 'boom' | 'blocked' {
  const def = ITEMS[piece.itemId];
  const slot = def?.equip?.slot;
  if (!slot) return 'blocked';
  if (scroll.target === 'weapon' && slot !== 'weapon') return 'blocked';
  if (scroll.target === 'armor' && slot === 'weapon') return 'blocked';
  if (piece.upgradesLeft <= 0) return 'blocked';
  if (rng() > scroll.success) {
    if (scroll.boom > 0 && rng() < scroll.boom) return 'boom';
    return 'fail';
  }
  piece.watk += scroll.watk ?? 0;
  piece.def += scroll.def ?? 0;
  piece.upgradesLeft -= 1;
  piece.successCount += 1;
  return 'success';
}
